import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useMotionValue, useSpring } from 'motion/react';
import { Tilt } from './Motion3D';

/* ------------------------------------------------------------------ */
/* SpotlightCard — gradient border + cursor-following radial highlight */
/* ------------------------------------------------------------------ */
export const SpotlightCard = ({ className = '', accent = 'accent-cyan', style, children, as: Tag = 'div', tilt = true, tiltMax = 7, ...rest }) => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - r.left}px`);
    el.style.setProperty('--my', `${e.clientY - r.top}px`);
  };
  const card = (
    <Tag ref={ref} onMouseMove={onMove} className={`spot-card ${accent} ${tilt ? '' : className}`} style={tilt ? { height: '100%' } : style} {...(tilt ? {} : rest)}>
      <div className="spot-card-inner">{children}</div>
    </Tag>
  );
  if (!tilt) return card;
  return (
    <Tilt className={className} style={style} max={tiltMax} {...rest}>
      {card}
    </Tilt>
  );
};

/* ------------------------------------------------------------------ */
/* Marquee — infinite horizontal scroll of children                    */
/* ------------------------------------------------------------------ */
export const Marquee = ({ children, speed = 40, reverse = false, className = '' }) => (
  <div className={`marquee ${className}`} style={{ '--marquee-speed': `${speed}s`, '--marquee-dir': reverse ? 'reverse' : 'normal' }}>
    <div className="marquee-track">
      <div className="marquee-group">{children}</div>
      <div className="marquee-group" aria-hidden="true">{children}</div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* CountUp — animates "$1.8T", "60%", "5+" style metrics into view      */
/* ------------------------------------------------------------------ */
const parseMetric = (value) => {
  const m = String(value).match(/^([^\d]*)([\d.,]+)(.*)$/);
  if (!m) return null;
  const num = parseFloat(m[2].replace(/,/g, ''));
  const decimals = (m[2].split('.')[1] || '').length;
  return { prefix: m[1], num, suffix: m[3], decimals };
};

export const CountUp = ({ value, duration = 1.6, className = '', style }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const parsed = parseMetric(value);
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: duration * 1000, bounce: 0 });
  const [display, setDisplay] = useState(parsed ? `${parsed.prefix}${(0).toFixed(parsed.decimals)}${parsed.suffix}` : value);

  useEffect(() => {
    if (!parsed) return undefined;
    const unsub = spring.on('change', (v) => {
      setDisplay(`${parsed.prefix}${v.toFixed(parsed.decimals)}${parsed.suffix}`);
    });
    return unsub;
  }, [spring, parsed]);

  useEffect(() => {
    if (inView && parsed) mv.set(parsed.num);
  }, [inView, parsed, mv]);

  return <span ref={ref} className={`metric ${className}`} style={style}>{parsed ? display : value}</span>;
};

/* ------------------------------------------------------------------ */
/* WindowFrame — browser/terminal chrome around content                */
/* ------------------------------------------------------------------ */
export const WindowFrame = ({ title, url, children, className = '', style, dark = true, right }) => (
  <div className={`win ${dark ? 'win-dark' : 'win-light'} ${className}`} style={style}>
    <div className="win-bar">
      <span className="win-dots"><i /><i /><i /></span>
      {url ? <span className="win-url">{url}</span> : title ? <span className="win-title">{title}</span> : <span />}
      <span className="win-right">{right}</span>
    </div>
    <div className="win-body">{children}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/* StatusPill — Live / Demo / In Development                            */
/* ------------------------------------------------------------------ */
const statusTone = (status) => {
  if (status === 'Live') return { color: '#34D399', label: status };
  if (status === 'Demo') return { color: '#FBBF24', label: status };
  return { color: '#60A5FA', label: status };
};

export const StatusPill = ({ status, className = '' }) => {
  const s = statusTone(status);
  return (
    <span className={`status-pill ${className}`} style={{ '--status': s.color }}>
      <i className="status-dot" />
      {s.label}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* Segmented — filter control with sliding indicator                   */
/* ------------------------------------------------------------------ */
export const Segmented = ({ options, value, onChange, className = '' }) => (
  <div className={`segmented ${className}`} role="tablist">
    {options.map((opt) => {
      const active = opt.value === value;
      return (
        <button
          key={opt.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(opt.value)}
          className={`segmented-item ${active ? 'active' : ''}`}
        >
          {active && (
            <motion.span
              layoutId="segmented-indicator"
              className="segmented-indicator"
              transition={{ type: 'spring', stiffness: 400, damping: 34 }}
            />
          )}
          <span className="relative z-10">{opt.label}</span>
        </button>
      );
    })}
  </div>
);

/* ------------------------------------------------------------------ */
/* LogoTile — tech logo with monogram fallback                          */
/* ------------------------------------------------------------------ */
const monogram = (name) => {
  const clean = name.replace(/^@/, '').replace(/[^A-Za-z0-9 ]/g, ' ').trim();
  const parts = clean.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
};

export const LogoTile = ({ name, logo, description, size = 'md' }) => {
  const [failed, setFailed] = useState(false);
  const showImg = logo && !failed;
  return (
    <div className={`logo-tile logo-tile-${size}`} title={description ? `${name} — ${description}` : name}>
      <div className="logo-tile-mark">
        {showImg ? (
          <img src={logo} alt="" loading="lazy" onError={() => setFailed(true)} />
        ) : (
          <span className="logo-tile-mono">{monogram(name)}</span>
        )}
      </div>
      <span className="logo-tile-name">{name}</span>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* SpecRow — label / value pair in a spec-sheet                        */
/* ------------------------------------------------------------------ */
export const SpecRow = ({ label, children, icon: Icon, accent }) => (
  <div className="spec-row">
    <div className="spec-label">
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent || 'var(--accent)' }} />}
      <span>{label}</span>
    </div>
    <div className="spec-value">{children}</div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Reveal — fade-up on scroll                                          */
/* ------------------------------------------------------------------ */
export const Reveal = ({ children, delay = 0, className = '', style, y = 24, depth = true, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y, rotateX: depth ? 10 : 0, transformPerspective: 1200 }}
    whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    style={{ transformOrigin: '50% 100%', ...style }}
    {...rest}
  >
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* ChapterDivider — bold full-width break between page chapters        */
/* ------------------------------------------------------------------ */
export const ChapterDivider = ({ number, title, subtitle, accent = '#22D3EE' }) => (
  <div className="chapter" style={{ '--chapter-accent': accent }}>
    <div className="chapter-rule" />
    <div className="chapter-body">
      <span className="chapter-number">{number}</span>
      <div>
        <h2 className="chapter-title">{title}</h2>
        {subtitle && <p className="chapter-sub">{subtitle}</p>}
      </div>
    </div>
    <div className="chapter-rule" />
  </div>
);
